/**
 * Deterministic role precedence for users WITHOUT an admin-assigned primary
 * role: the highest-precedence held role wins. Mirrors the backend list in
 * Core/Constants/DashboardRecipes.cs (RolePrecedence) — keep the two in sync.
 * Generalizes the old defaultRoute cascade (ADMIN > E-series > B > A > C > D).
 */
export const ROLE_PRECEDENCE: readonly string[] = [
  "ADMIN",
  "E1", "E2", "E3", "E4", "E5", "E1-VM", "E2-VM",
  "B1", "B2", "B3", "B4", "B5",
  "A1", "A2", "A3", "A4",
  "C0", "C1", "C2", "C3", "C4", "C5",
  "D1",
  // Buyer CRM (PR 2a) — F1 Buyer Manager, F2 Buyer Consultant. Appended rather
  // than interleaved so a new pipeline does not silently outrank a role someone
  // already holds.
  "F1", "F2",
];

/** Highest-precedence role the user holds, or null when they hold none. */
export function fallbackRole(roleCodes: readonly string[] | undefined | null): string | null {
  if (!roleCodes || roleCodes.length === 0) return null;
  return ROLE_PRECEDENCE.find((r) => roleCodes.includes(r)) ?? roleCodes[0] ?? null;
}

/** The effective primary role: the admin-assigned one when set, else the
 *  deterministic precedence fallback. */
export function effectivePrimaryRole(user: {
  primaryRoleCode?: string | null;
  roleCodes?: readonly string[];
} | null | undefined): string | null {
  if (!user) return null;
  if (user.primaryRoleCode && user.roleCodes?.includes(user.primaryRoleCode)) {
    return user.primaryRoleCode;
  }
  return fallbackRole(user.roleCodes);
}
